import React, { useState, useContext } from 'react';
import { AuthContext } from '../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import './OnboardingModal.css';

const STEPS = [
  {
    id: "primaryGoal",
    label: "Step 1 of 6",
    title: "What are you learning for?",
    sub: "We'll customize your track recommendations.",
    options: [
      { icon: "🎯", label: "Get a software internship", value: "internship" },
      { icon: "🚀", label: "Build projects for my portfolio", value: "projects" },
      { icon: "💼", label: "Prepare for placements", value: "placement" },
      { icon: "🔬", label: "Get into VLSI / chip design", value: "vlsi" },
      { icon: "📈", label: "Upskill while in college", value: "upskill" },
      { icon: "🌍", label: "Freelance / build my own product", value: "freelance" },
    ],
  },
  {
    id: "experienceLevel",
    label: "Step 2 of 6",
    title: "How comfortable are you with coding?",
    sub: "This helps us adjust the difficulty curve.",
    options: [
      { icon: "🌱", label: "Beginner — still learning basics", value: "beginner" },
      { icon: "⚡", label: "Intermediate — built a few things", value: "intermediate" },
      { icon: "🔥", label: "Advanced — looking to specialize", value: "advanced" },
    ],
  },
  {
    id: "timeline",
    label: "Step 3 of 6",
    title: "When do you need results by?",
    sub: "We'll calculate your learning velocity.",
    options: [
      { icon: "🏃", label: "Less than 3 months — urgent", value: "urgent" },
      { icon: "📅", label: "3–6 months", value: "3-6months" },
      { icon: "🎓", label: "Next academic year", value: "next-year" },
      { icon: "🤷", label: "Just exploring for now", value: "exploring" },
    ],
  },
  {
    id: "dailyCommitment",
    label: "Step 4 of 6",
    title: "How much time can you commit daily?",
    sub: "We'll pace your tracks to match your schedule.",
    options: [
      { icon: "⏱️", label: "15–30 minutes", value: "30min" },
      { icon: "⏰", label: "30–60 minutes", value: "1hr" },
      { icon: "🕐", label: "1–2 hours", value: "2hr" },
      { icon: "🔥", label: "2+ hours (intensive)", value: "2hr+" },
    ],
  },
  {
    id: "focusArea",
    label: "Step 5 of 6",
    title: "What are you focused on?",
    sub: "Choose the domain you want to master.",
    options: [
      { icon: "🖥️", label: "Frontend (React, HTML/CSS)", value: "frontend" },
      { icon: "⚙️", label: "Backend (Node, Python, APIs)", value: "backend" },
      { icon: "🌐", label: "Full Stack", value: "fullstack" },
      { icon: "🔬", label: "VLSI / RTL / Chip Design", value: "vlsi" },
      { icon: "🧮", label: "Data Structures & Algorithms", value: "dsa" },
      { icon: "🤖", label: "Machine Learning / AI", value: "ml-ai" },
      { icon: "📱", label: "Mobile (Android/iOS)", value: "mobile" },
    ],
  },
  {
    id: "studyTimeTarget",
    label: "Step 6 of 6",
    title: "When do you prefer to study?",
    sub: "We'll send your daily learning reminders then.",
    options: [
      { icon: "🌅", label: "Early morning — 6:00 AM", value: "06:00" },
      { icon: "☀️", label: "Late morning — 10:00 AM", value: "10:00" },
      { icon: "🏙️", label: "Afternoon — 2:00 PM", value: "14:00" },
      { icon: "🌆", label: "Evening — 6:00 PM", value: "18:00" },
      { icon: "🌙", label: "Night — 9:00 PM", value: "21:00" },
    ],
  },
];

export default function OnboardingModal({ onComplete }) {
  const { checkAuth } = useContext(AuthContext);
  const navigate = useNavigate();

  const [currentStepIndex, setCurrentStepIndex] = useState(0);
  const [formData, setFormData] = useState({
    primaryGoal: "",
    experienceLevel: "",
    timeline: "",
    dailyCommitment: "",
    focusArea: "",
    studyTimeTarget: "",
  });
  const [loading, setLoading] = useState(false);
  const [showUpgradePitch, setShowUpgradePitch] = useState(false);

  const handleSelect = (value) => {
    const currentStep = STEPS[currentStepIndex];
    const newFormData = { ...formData, [currentStep.id]: value };
    setFormData(newFormData);

    if (currentStepIndex < STEPS.length - 1) {
      setCurrentStepIndex((c) => c + 1);
    } else {
      finishOnboarding(newFormData);
    }
  };

  const finishOnboarding = async (finalData) => {
    setLoading(true);
    try {
      let targetRole = "Software Engineer";
      if (finalData.focusArea === "frontend") targetRole = "Frontend Developer";
      if (finalData.focusArea === "backend") targetRole = "Backend Developer";
      if (finalData.focusArea === "fullstack") targetRole = "Full Stack Developer";
      if (finalData.focusArea === "vlsi") targetRole = "VLSI Engineer";
      if (finalData.primaryGoal === "internship") targetRole += " Intern";

      const payload = { ...finalData, targetRole };

      await axios.put(
        `${process.env.REACT_APP_API_URL || "/api"}/profile/onboarding`,
        payload,
        { withCredentials: true }
      );

      await checkAuth();

      const isUrgent =
        finalData.timeline === "urgent" ||
        finalData.primaryGoal === "internship" ||
        finalData.primaryGoal === "placement";

      if (isUrgent) {
        setShowUpgradePitch(true);
      } else {
        if (onComplete) onComplete();
      }
    } catch (err) {
      console.error("Onboarding saving failed", err);
    } finally {
      setLoading(false);
    }
  };

  /* ── Upgrade pitch ── */
  if (showUpgradePitch) {
    return (
      <div className="ob-overlay">
        <div className="ob-card pitch-card">
          <div className="pitch-badge">Fast-track your goal</div>
          <h2 className="pitch-title">Your goal is urgent.</h2>
          <p className="pitch-sub">
            Pro users complete tracks <strong>3×&nbsp;faster</strong> with AI
            quizzes and all phases unlocked instantly.
          </p>
          <div className="pitch-price">₹499</div>
          <p className="pitch-price-note">first month · cancel anytime</p>
          <ul className="pitch-features">
            {[
              "AI-powered quizzes after every phase",
              "All track phases unlocked instantly",
              "Priority roadmap updates",
              "Progress streak reminders",
            ].map((f) => (
              <li key={f} className="pitch-feat">
                <span className="pitch-feat-dot" />
                {f}
              </li>
            ))}
          </ul>
          <button className="pitch-cta" onClick={() => navigate("/upgrade")}>
            Unlock Pro now
          </button>
          <button
            className="pitch-skip"
            onClick={() => { if (onComplete) onComplete(); }}
          >
            No thanks, I'll go at normal speed
          </button>
        </div>
      </div>
    );
  }

  /* ── Loading ── */
  if (loading) {
    return (
      <div className="ob-overlay">
        <div className="ob-card">
          <div className="ob-loading">
            <div className="ob-spinner" />
            <p className="ob-loading-text">Saving your profile...</p>
          </div>
        </div>
      </div>
    );
  }

  /* ── Steps ── */
  const currentStep = STEPS[currentStepIndex];

  return (
    <div className="ob-overlay">
      <div className="ob-card">
        {/* Progress dots */}
        <div className="ob-dots">
          {STEPS.map((_, i) => (
            <div
              key={i}
              className={`ob-dot ${i < currentStepIndex
                  ? "done"
                  : i === currentStepIndex
                    ? "active"
                    : "future"
                }`}
            />
          ))}
        </div>

        <p className="ob-step-label">{currentStep.label}</p>
        <h2 className="ob-title">{currentStep.title}</h2>
        <p className="ob-sub">{currentStep.sub}</p>

        <div className="ob-options">
          {currentStep.options.map((opt) => (
            <button
              key={opt.value}
              className="ob-opt"
              onClick={() => handleSelect(opt.value)}
            >
              <span className="ob-opt-icon">{opt.icon}</span>
              <span>{opt.label}</span>
            </button>
          ))}
        </div>

        {currentStepIndex > 0 && (
          <button
            className="ob-back"
            onClick={() => setCurrentStepIndex((c) => c - 1)}
          >
            ← Back
          </button>
        )}
      </div>
    </div>
  );
}